import { Modal } from '@carouselgg/nexus/modal'
import { Button } from '@carouselgg/nexus/button'
import { Text } from '@carouselgg/nexus/text'
import { Callout } from '@carouselgg/nexus/callout'
import { TrustGauge } from '@carouselgg/nexus/trust-gauge'
import { ChoiceList, Choice } from '@carouselgg/nexus/choice-list'
import { ArrowRight, X } from '@carouselgg/nexus/icon'
import { Link } from 'react-router'
import type { TrustStanding } from '../../api/compete'
import { tipsToReach } from './labels'
import styles from './TrustHelpModal.module.css'

/**
 * A tip with somewhere to go is a link (closing the panel on the way); "play
 * more ranked matches" has nowhere to go and stays a plain row.
 */
const tipLink = (url: string, onClose: () => void) => (url ? { to: url, onClick: onClose } : {})

/**
 * How this player could reach a competition's Trust Score minimum: the gap,
 * then their own tips (from what their account is actually missing), with the
 * fewest that would close it marked as the place to start.
 */
export function TrustHelpModal({ open, onClose, standing, min, gameName }: {
  open: boolean
  onClose: () => void
  standing: TrustStanding
  min: number
  gameName: string
}) {
  const gap = Math.max(0, min - standing.score)
  const recommended = new Set(tipsToReach(standing.tips, gap))
  const reachable = standing.tips.reduce((sum, t) => sum + t.points, 0) >= gap
  const others = standing.tips.filter((t) => !recommended.has(t))

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Reach a Trust Score of ${min}`}
      size="md"
      footer={
        <>
          <Link to={`/trust?required=${min}&redirect=${encodeURIComponent('/compete')}`} className={styles.guide}>
            Full Trust Score guide <ArrowRight size={14} />
          </Link>
          <Button variant="ghost" size="sm" onClick={onClose}><X size={16} /> Close</Button>
        </>
      }
    >
      <div className={styles.body}>
        <div className={styles.gap}>
          <TrustGauge score={standing.score} minRequired={min} size={56} />
          <div>
            <Text size="base" weight="semibold">
              {standing.scope === 'game' ? `Your ${gameName} Trust Score` : 'Your Trust Score'} is {standing.score}
            </Text>
            <Text size="sm" muted>{gap} {gap === 1 ? 'point' : 'points'} to go to reach {min}.</Text>
          </div>
        </div>

        {standing.scope === 'game' && (
          <Text size="sm" muted>
            Competitions judge the score for the game they're played in: your verified {gameName} history plus your Carousel identity.
          </Text>
        )}
        {standing.low_game_specific && (
          <Callout variant="info" icon>
            Your account is well established, but it has little {gameName} history yet. Linking a verified {gameName} account and playing ranked matches is what moves this score.
          </Callout>
        )}

        {recommended.size > 0 && (
          <section className={styles.section}>
            <Text size="xs" weight="semibold" muted className={styles.heading}>
              {reachable ? 'Start here. These would get you there' : 'Start here'}
            </Text>
            <ChoiceList aria-label="Recommended ways to raise your Trust Score">
              {[...recommended].map((tip) => (
                <Choice key={`${tip.category}-${tip.label}`} label={tip.label} hint={`+${tip.points} points`} {...tipLink(tip.action_url, onClose)} />
              ))}
            </ChoiceList>
          </section>
        )}
        {others.length > 0 && (
          <section className={styles.section}>
            <Text size="xs" weight="semibold" muted className={styles.heading}>Also helps</Text>
            <ChoiceList aria-label="Other ways to raise your Trust Score">
              {others.map((tip) => (
                <Choice key={`${tip.category}-${tip.label}`} label={tip.label} hint={`+${tip.points} points`} {...tipLink(tip.action_url, onClose)} />
              ))}
            </ChoiceList>
          </section>
        )}
        {!reachable && (
          <Text size="sm" muted>
            These won't close the whole gap on their own. The rest comes from playing: ranked {gameName} matches and Carousel competitions both build the score over time.
          </Text>
        )}
      </div>
    </Modal>
  )
}
